import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import { registerTrainingFonts, PDF_LOGO } from '@gng-ehs/shared';
import type { Form11Certificate } from '@gng-ehs/shared/types';

export interface Form11IndexItem extends Form11Certificate {
  company_name?: string;
  company_short_code?: string;
  inspector_name?: string;
}

export interface Form11IndexExportOptions {
  companyName: string;
  items: Form11IndexItem[];
  periodLabel?: string;
}

/**
 * Format a date string (YYYY-MM-DD or DD/MM/YYYY or ISO) into DD-MM-YYYY with hyphens.
 */
export function formatIndexDate(dateStr?: string | null): string {
  if (!dateStr || dateStr === 'First time by us' || dateStr === 'Inaccessible' || dateStr === 'N/A') {
    return '';
  }
  try {
    const clean = dateStr.trim();
    if (clean.includes('-')) {
      const parts = clean.split('T')[0].split('-');
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          // YYYY-MM-DD
          return `${parts[2].padStart(2, '0')}-${parts[1].padStart(2, '0')}-${parts[0]}`;
        } else {
          // DD-MM-YYYY
          return `${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}-${parts[2]}`;
        }
      }
    } else if (clean.includes('/')) {
      const parts = clean.split('/');
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          // YYYY/MM/DD
          return `${parts[2].padStart(2, '0')}-${parts[1].padStart(2, '0')}-${parts[0]}`;
        } else {
          // DD/MM/YYYY
          return `${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}-${parts[2]}`;
        }
      }
    } else if (clean.includes('.')) {
      const parts = clean.split('.');
      if (parts.length === 3) {
        return `${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}-${parts[2]}`;
      }
    }
    return clean;
  } catch {
    return dateStr || '';
  }
}

/**
 * Extract hydro test date from Form 11 item (from last_hydraulic_exam_details, last_external_exam_date, or examination_date).
 */
export function extractHydroTestDate(item: Form11IndexItem): string {
  if (item.last_hydraulic_exam_details) {
    // Look for dates like 04/01/2026 or 04-01-2026 or 2026-01-04
    const match = item.last_hydraulic_exam_details.match(/(\d{2}[-/.]\d{2}[-/.]\d{4}|\d{4}[-/.]\d{2}[-/.]\d{2})/);
    if (match) {
      return formatIndexDate(match[1]);
    }
  }
  if (item.last_external_exam_date && item.last_external_exam_date !== 'First time by us') {
    return formatIndexDate(item.last_external_exam_date);
  }
  return formatIndexDate(item.examination_date);
}

/**
 * Calculate hydro test due date (from hydro_test_due_date, or hydro test date + 2 years - 1 day).
 */
export function extractHydroDueDate(item: Form11IndexItem, hydroTestDateStr: string): string {
  if (item.hydro_test_due_date) {
    return formatIndexDate(item.hydro_test_due_date);
  }
  if (!hydroTestDateStr) return '';
  try {
    const parts = hydroTestDateStr.split('-');
    if (parts.length === 3) {
      const d = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10) - 1;
      const y = parseInt(parts[2], 10);
      const dateObj = new Date(y, m, d);
      dateObj.setFullYear(dateObj.getFullYear() + 2);
      dateObj.setDate(dateObj.getDate() - 1);
      const dd = String(dateObj.getDate()).padStart(2, '0');
      const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
      const yy = dateObj.getFullYear();
      return `${dd}-${mm}-${yy}`;
    }
  } catch {
    // fallback
  }
  return '';
}

/**
 * Extract thickness / ultrasonic test date.
 */
export function extractThicknessTestDate(item: Form11IndexItem): string {
  if (item.last_ultrasonic_exam_date && item.last_ultrasonic_exam_date !== 'First time by us') {
    return formatIndexDate(item.last_ultrasonic_exam_date);
  }
  return formatIndexDate(item.examination_date);
}

/**
 * Extract thickness due test date (from next_inspection_date, or thickness test date + 6 months - 1 day).
 */
export function extractThicknessDueDate(item: Form11IndexItem, thicknessTestDateStr: string): string {
  if (item.next_inspection_date) {
    return formatIndexDate(item.next_inspection_date);
  }
  if (!thicknessTestDateStr) return '';
  try {
    const parts = thicknessTestDateStr.split('-');
    if (parts.length === 3) {
      const d = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10) - 1;
      const y = parseInt(parts[2], 10);
      const dateObj = new Date(y, m, d);
      dateObj.setMonth(dateObj.getMonth() + 6);
      dateObj.setDate(dateObj.getDate() - 1);
      const dd = String(dateObj.getDate()).padStart(2, '0');
      const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
      const yy = dateObj.getFullYear();
      return `${dd}-${mm}-${yy}`;
    }
  } catch {
    // fallback
  }
  return '';
}

/**
 * Generate a client-side Form 11 Index PDF (Landscape A4) matching the sample index document.
 * Returns a Blob URL for preview / download.
 */
export function generateForm11IndexPDF(options: Form11IndexExportOptions): string {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  registerTrainingFonts(doc);
  const { items } = options;

  const PAGE_WIDTH = 297;
  const PAGE_HEIGHT = 210;
  const MARGIN_LEFT = 18;
  const MARGIN_RIGHT = 18;
  const MARGIN_TOP = 12;
  const MARGIN_BOTTOM = 14;

  const TABLE_WIDTH = PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT; // 261 mm

  // Column widths matching sample proportions
  // Col 1: Sr. No (18mm)
  // Col 2: Deatiles (93mm)
  // Col 3: Hydro Test Date (37mm)
  // Col 4: Hydro Test Due Date (37mm)
  // Col 5: Thicknees Test Date (38mm)
  // Col 6: Thicknees Due Test Date (38mm)
  const COL_WIDTHS = [18, 93, 37, 37, 38, 38];
  const COL_X = [
    MARGIN_LEFT,
    MARGIN_LEFT + COL_WIDTHS[0],
    MARGIN_LEFT + COL_WIDTHS[0] + COL_WIDTHS[1],
    MARGIN_LEFT + COL_WIDTHS[0] + COL_WIDTHS[1] + COL_WIDTHS[2],
    MARGIN_LEFT + COL_WIDTHS[0] + COL_WIDTHS[1] + COL_WIDTHS[2] + COL_WIDTHS[3],
    MARGIN_LEFT + COL_WIDTHS[0] + COL_WIDTHS[1] + COL_WIDTHS[2] + COL_WIDTHS[3] + COL_WIDTHS[4],
  ];

  const drawPageHeader = (yPos: number): number => {
    doc.addImage(PDF_LOGO, 'PNG', MARGIN_LEFT, yPos, 20, 13);
    doc.setFont('Inter', 'bold'); doc.setFontSize(19); doc.setTextColor(96, 160, 28);
    doc.text('G & G  E H S', MARGIN_LEFT + 25, yPos + 5);
    doc.setTextColor(20, 64, 38); doc.setFontSize(12);
    doc.text('C O N S U L T A N T', MARGIN_LEFT + 25, yPos + 12);
    doc.setFont('Inter', 'normal'); doc.setFontSize(8);
    doc.text('Safety, Environment & Fire Consultant and Suppliers', MARGIN_LEFT, yPos + 18);
    doc.setTextColor(17, 24, 39);
    doc.text(['LR-210, Park Paradise, Beside Billa Bong School,', 'Vadsar-Kalali Road, Vadsar, Vadodara.', 'Email: gngehs@yahoo.com | Mob.: 9904000309, 8153894604, 9824768824'], 165, yPos + 3);
    doc.setFont('Inter', 'bold'); doc.setFontSize(9);
    const companyLines = doc.splitTextToSize(options.companyName || 'Form 11 - Examination index', TABLE_WIDTH);
    doc.text(companyLines, MARGIN_LEFT, yPos + 25);
    const tableStartY = yPos + 28 + companyLines.length * 3.7;

    // 2. Draw Table Header with bright yellow background (#FFFF00)
    const headerHeight = 12;
    doc.setFillColor(255, 255, 0); // Yellow #FFFF00
    doc.rect(MARGIN_LEFT, tableStartY, TABLE_WIDTH, headerHeight, 'F');

    // Draw header border & column separators
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.35);
    doc.rect(MARGIN_LEFT, tableStartY, TABLE_WIDTH, headerHeight, 'S');

    for (let i = 1; i < COL_X.length; i++) {
      doc.line(COL_X[i], tableStartY, COL_X[i], tableStartY + headerHeight);
    }

    // Header text
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);

    // Col 1: Sr. No
    doc.text('Sr. No', COL_X[0] + COL_WIDTHS[0] / 2, tableStartY + 7.5, { align: 'center' });

    // Col 2: Deatiles (spelled exactly as in sample)
    doc.text('Details', COL_X[1] + COL_WIDTHS[1] / 2, tableStartY + 7.5, { align: 'center' });

    // Col 3: Hydro Test Date (2 lines)
    doc.text('Hydro Test', COL_X[2] + COL_WIDTHS[2] / 2, tableStartY + 4.8, { align: 'center' });
    doc.text('Date', COL_X[2] + COL_WIDTHS[2] / 2, tableStartY + 9.5, { align: 'center' });

    // Col 4: Hydro Test Due Date (2 lines)
    doc.text('Hydro Test', COL_X[3] + COL_WIDTHS[3] / 2, tableStartY + 4.8, { align: 'center' });
    doc.text('Due Date', COL_X[3] + COL_WIDTHS[3] / 2, tableStartY + 9.5, { align: 'center' });

    // Col 5: Thicknees Test Date (2 lines, exact spelling from sample)
    doc.text('Thickness Test', COL_X[4] + COL_WIDTHS[4] / 2, tableStartY + 4.8, { align: 'center' });
    doc.text('Date', COL_X[4] + COL_WIDTHS[4] / 2, tableStartY + 9.5, { align: 'center' });

    // Col 6: Thicknees Due Test Date (2 lines, exact spelling from sample)
    doc.text('Thickness Due Test', COL_X[5] + COL_WIDTHS[5] / 2, tableStartY + 4.8, { align: 'center' });
    doc.text('Date', COL_X[5] + COL_WIDTHS[5] / 2, tableStartY + 9.5, { align: 'center' });

    return tableStartY + headerHeight;
  };

  let currentY = drawPageHeader(MARGIN_TOP);

  // Render Table Rows
  items.forEach((item, index) => {
    const srNo = String(index + 1);

    const vesselTitle = (item.vessel_name_description || 'Air Receiver').trim();
    const srNoAndCap = `Sr. No.: ${item.vessel_serial_no || '—'}, Cap.: ${item.vessel_capacity || '—'}`;
    const location = `Location: ${item.vessel_location || '—'}`;

    const hydroDate = extractHydroTestDate(item);
    const hydroDueDate = extractHydroDueDate(item, hydroDate);
    const thicknessDate = extractThicknessTestDate(item);
    const thicknessDueDate = extractThicknessDueDate(item, thicknessDate);

    doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5);
    const titleLines = doc.splitTextToSize(vesselTitle, COL_WIDTHS[1] - 6);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
    const detailLines = doc.splitTextToSize(`${srNoAndCap}\n${location}`, COL_WIDTHS[1] - 6);
    const rowHeight = Math.max(16.5, 5 + titleLines.length * 4 + detailLines.length * 3.5);

    // Check if new page is needed
    if (currentY + rowHeight > PAGE_HEIGHT - MARGIN_BOTTOM) {
      doc.addPage('a4', 'landscape');
      currentY = drawPageHeader(MARGIN_TOP);
    }

    // Draw row bounding box & vertical cell dividers
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.35);
    doc.rect(MARGIN_LEFT, currentY, TABLE_WIDTH, rowHeight, 'S');

    for (let i = 1; i < COL_X.length; i++) {
      doc.line(COL_X[i], currentY, COL_X[i], currentY + rowHeight);
    }

    // Cell 1: Sr. No (Centered)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(srNo, COL_X[0] + COL_WIDTHS[0] / 2, currentY + 9.5, { align: 'center' });

    // Cell 2: Deatiles (3 lines, left aligned with 3mm indent)
    const textLeft = COL_X[1] + 3;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.text(titleLines, textLeft, currentY + 4.8, { lineHeightFactor: 1.19 });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.text(detailLines, textLeft, currentY + 4.8 + titleLines.length * 4, { lineHeightFactor: 1.17 });

    // Cell 3: Hydro Test Date (Centered)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.text(hydroDate || '—', COL_X[2] + COL_WIDTHS[2] / 2, currentY + 9.5, { align: 'center' });

    // Cell 4: Hydro Test Due Date (Centered)
    doc.text(hydroDueDate || '—', COL_X[3] + COL_WIDTHS[3] / 2, currentY + 9.5, { align: 'center' });

    // Cell 5: Thicknees Test Date (Centered)
    doc.text(thicknessDate || '—', COL_X[4] + COL_WIDTHS[4] / 2, currentY + 9.5, { align: 'center' });

    // Cell 6: Thicknees Due Test Date (Centered)
    doc.text(thicknessDueDate || '—', COL_X[5] + COL_WIDTHS[5] / 2, currentY + 9.5, { align: 'center' });

    currentY += rowHeight;
  });

  const pdfBlob = doc.output('blob');
  return URL.createObjectURL(pdfBlob);
}

/**
 * Generate and download Form 11 Index as an Excel spreadsheet (.xlsx).
 */
export function exportForm11IndexToExcel(options: Form11IndexExportOptions): void {
  const { companyName, items, periodLabel = 'Last_6_Months' } = options;

  const excelRows = items.map((item, index) => {
    const vesselTitle = (item.vessel_name_description || 'Air Receiver').trim();
    const srNoAndCap = `Sr. No.: ${item.vessel_serial_no || '—'}, Cap.: ${item.vessel_capacity || '—'}`;
    const location = `Location: ${item.vessel_location || '—'}`;

    const hydroDate = extractHydroTestDate(item);
    const hydroDueDate = extractHydroDueDate(item, hydroDate);
    const thicknessDate = extractThicknessTestDate(item);
    const thicknessDueDate = extractThicknessDueDate(item, thicknessDate);

    return {
      'Sr. No': index + 1,
      'Details': `${vesselTitle}\n${srNoAndCap}\n${location}`,
      'Hydro Test Date': hydroDate || '',
      'Hydro Test Due Date': hydroDueDate || '',
      'Thicknees Test Date': thicknessDate || '',
      'Thicknees Due Test Date': thicknessDueDate || '',
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(excelRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Form 11 Index');

  worksheet['!cols'] = [
    { wch: 8 },  // Sr. No
    { wch: 45 }, // Deatiles
    { wch: 18 }, // Hydro Test Date
    { wch: 20 }, // Hydro Test Due Date
    { wch: 20 }, // Thicknees Test Date
    { wch: 22 }, // Thicknees Due Test Date
  ];

  const cleanCompanyName = (companyName || 'Company').replace(/[^a-zA-Z0-9_-]/g, '_');
  const todayStr = new Date().toISOString().split('T')[0];
  const filename = `Form11_Index_${cleanCompanyName}_${periodLabel}_${todayStr}.xlsx`;

  XLSX.writeFile(workbook, filename);
}
